import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list.
 *
 * When the `defer-unavailable-products` attribute is present (infinite-scroll
 * collections only), unavailable products are held in a deferred queue and
 * appended as a single batch only after all available products across every
 * page have been loaded. This prevents any visible card movement.
 *
 * The Liquid template must cooperate by emitting unavailable products inside
 * a `<template data-deferred-page="N">` element within the grid, and
 * available products as normal `<li ref="cards[]">` items.
 */
export default class ResultsList extends PaginatedList {
  // ── Deferred-unavailable state ────────────────────────────────────────────

  /** @type {Element[]} */
  #deferredItems = [];

  /** @type {number} page number to fetch/render next */
  #nextPageCursor = 1;

  /** @type {boolean} */
  #deferEnabled = false;

  /** @type {boolean} guard against concurrent cascade runs */
  #renderInProgress = false;

  /** @type {IntersectionObserver | null} */
  #deferObserver = null;

  // ─────────────────────────────────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();

    this.#deferEnabled = this.hasAttribute('defer-unavailable-products');

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.#deferEnabled) {
      this.#initCursor();
      this.#collectInitialDeferredItems();
      this.#installDeferObserver();

      // Belt-and-suspenders: clear deferred state immediately on any filter
      // event so stale items never bleed into the new result set.
      document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdateImmediate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);

    if (this.#deferEnabled) {
      document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdateImmediate);
    }

    this.#deferObserver?.disconnect();
    this.#deferObserver = null;
    this.#deferredItems = [];
  }

  /**
   * Called by PaginatedList after the grid has been updated with fresh
   * first-page content following a filter/sort change.
   */
  onFilterReset() {
    if (!this.#deferEnabled) return;

    // Disconnect old deferred observer immediately so PaginatedList's newly
    // re-registered infinityScrollObserver doesn't race with ours.
    this.#deferObserver?.disconnect();
    this.#deferObserver = null;
    this.#renderInProgress = false;

    // Un-observe viewMoreNext from PaginatedList's observer (best-effort with
    // current refs; rAF handles the case where refs update after this tick).
    const { viewMoreNext } = this.refs;
    if (viewMoreNext) this.infinityScrollObserver?.unobserve(viewMoreNext);

    requestAnimationFrame(() => {
      // Refs are stable now — ensure viewMoreNext is unobserved from parent.
      const { viewMoreNext: freshNext } = this.refs;
      if (freshNext) this.infinityScrollObserver?.unobserve(freshNext);

      this.#initCursor();
      this.#collectInitialDeferredItems();
      this.#installDeferObserver();
    });
  }

  // ── Deferred-unavailable internals ───────────────────────────────────────

  /**
   * Clears deferred state immediately when a filter event fires so stale
   * items never survive into the next render cycle.
   */
  #onFilterUpdateImmediate = () => {
    this.#deferredItems = [];
    this.#renderInProgress = false;
  };

  /**
   * Initialise #nextPageCursor from the last visible card's data-page, or
   * fall back to URL ?page param + 1.
   */
  #initCursor() {
    const { grid } = this.refs;
    if (grid) {
      const allCards = grid.querySelectorAll(':scope > [data-page]');
      if (allCards.length > 0) {
        const lastCard = /** @type {HTMLElement} */ (allCards[allCards.length - 1]);
        this.#nextPageCursor = Number(lastCard.dataset.page ?? 1) + 1;
        return;
      }
    }
    const urlPage = parseInt(new URL(window.location.href).searchParams.get('page') ?? '1', 10);
    this.#nextPageCursor = (Number.isFinite(urlPage) ? urlPage : 1) + 1;
  }

  /**
   * Extracts unavailable items from the `<template data-deferred-page>` that
   * the server renders into the initial grid, then removes the template node.
   */
  #collectInitialDeferredItems() {
    const { grid } = this.refs;
    if (!grid) return;

    const template = /** @type {HTMLTemplateElement | null} */ (
      grid.querySelector(':scope > template[data-deferred-page]')
    );
    if (!template) return;

    // Move elements out of template.content before removing the template.
    this.#deferredItems.push(...Array.from(template.content.children));
    template.remove();
  }

  /**
   * Sets up our own IntersectionObserver on viewMoreNext after un-registering
   * PaginatedList's observer for that element.
   */
  #installDeferObserver() {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext) return;

    // Hand off viewMoreNext from PaginatedList's observer to ours.
    this.infinityScrollObserver?.unobserve(viewMoreNext);

    this.#deferObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#renderNext();
          }
        }
      },
      { rootMargin: '100px' }
    );

    this.#deferObserver.observe(viewMoreNext);
  }

  async #renderNext() {
    if (this.#renderInProgress) return;
    this.#renderInProgress = true;
    try {
      await this.#cascade();
    } finally {
      this.#renderInProgress = false;
    }
  }

  /**
   * Core cascade loop.
   *
   * Fetches pages one at a time. Available items are appended immediately and
   * control returns to the scroll observer. Unavailable items accumulate in
   * #deferredItems. When the final page is exhausted, all deferred items are
   * flushed to the bottom of the grid at once.
   */
  async #cascade() {
    const lastPage = this.getLastPage();

    while (this.#nextPageCursor <= lastPage) {
      const pageNumber = this.#nextPageCursor;
      const url = this.#pageUrl(pageNumber);
      const historyUrl = url.toString();

      // Fetch the page if not already cached by PaginatedList's pre-fetch.
      if (!this.pages.has(pageNumber)) {
        await this.fetchPage(pageNumber, url);
      }

      // If fetch failed (network error, page cleared, etc.) abort the cascade.
      if (!this.pages.has(pageNumber)) break;

      const { available, deferred } = this.#splitPage(pageNumber);

      this.#deferredItems.push(...deferred);
      this.#nextPageCursor++;

      if (available.length > 0) {
        const { grid } = this.refs;
        if (!grid) break;

        grid.append(...available);
        this.processAspectRatios();
        history.pushState('', '', historyUrl);

        // Pre-fetch the following page in the background.
        const prefetchCursor = this.#nextPageCursor;
        if (prefetchCursor <= lastPage) {
          requestIdleCallback(() => {
            this.fetchPage(prefetchCursor, this.#pageUrl(prefetchCursor));
          });
        }

        // Stop cascade; the observer fires again when the user scrolls further.
        return;
      }

      // Page had zero available items — continue cascade immediately.
    }

    // All pages scanned: flush every deferred item at once.
    this.#flushDeferred();
  }

  #flushDeferred() {
    const { grid } = this.refs;
    if (!grid || !this.#deferredItems.length) return;

    grid.append(...this.#deferredItems);
    this.processAspectRatios();
    this.#deferredItems = [];

    this.#deferObserver?.disconnect();
    this.#deferObserver = null;
  }

  /**
   * Parses the cached HTML for `pageNumber` and returns:
   *   available — the visible `<li ref="cards[]">` elements
   *   deferred  — items from the `<template data-deferred-page>` (cloned)
   *
   * @param {number} pageNumber
   * @returns {{ available: Element[], deferred: Element[] }}
   */
  #splitPage(pageNumber) {
    const html = this.pages.get(pageNumber);
    if (!html) return { available: [], deferred: [] };

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const gridEl = doc.querySelector('[ref="grid"]');
    if (!gridEl) return { available: [], deferred: [] };

    const available = Array.from(gridEl.querySelectorAll(':scope > [ref="cards[]"]'));

    const template = /** @type {HTMLTemplateElement | null} */ (
      gridEl.querySelector(':scope > template[data-deferred-page]')
    );
    const deferred = template
      ? Array.from(template.content.children).map((el) => el.cloneNode(true))
      : [];

    return { available, deferred };
  }

  /**
   * @param {number} page
   * @returns {URL}
   */
  #pageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';
    return url;
  }

  // ── Layout controls (unchanged) ───────────────────────────────────────────

  /**
   * Updates the layout.
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
